import GeneralCustomersCard from '@/components/CustomerCard/CustomerCard';
import { Column, Row } from '@/components/SettingsLayout';
import { Text, Divider, Icon, IconType } from '@/ui';
import { useTranslation } from 'react-i18next';
import dashboardStyles from '../../dashboard/styles.module.css';
import styles from '../styles.module.css';
import useFormatNumber from '@/hooks/useFormatNumber';
import { formatCurrency } from '@/utils/currency';
import { useNavigate, useParams } from 'react-router-dom';
import { Country } from 'country-state-city';
import { Company } from '@/context/types';
import { convertDateToFormat, formatDateToNow } from '@/utils';
import { CompanyLinks } from '../types';
import { TBuild, User } from '@/types';
import CheckoutFlowCard from '@/components/CheckoutFlowCard/CheckoutFlowCard';
import { Plan } from '@/services/Plans/plans.service';
import { CompanyDashboardData } from '@/services';
import { CurrencyCode } from '@final-commerce/common';

type Props = {
  company: Company;
  dashboardData?: CompanyDashboardData;
  companyOwner?: User;
  builds: TBuild[];
  plan?: Plan;
};
function CompanyDashboard({ company, dashboardData, companyOwner, builds, plan }: Props) {
  const { t } = useTranslation();
  const formatNumber = useFormatNumber();
  const navigate = useNavigate();
  const { id } = useParams();

  return (
    <Column gapSize='32'>
      <div className={`${styles.cardGrid}`}>
        <div className={dashboardStyles.blueSvg}>
          <GeneralCustomersCard
            title={t('Dashboard.plan')}
            comment={t('Dashboard.plan_comment')}
            data={plan?.name ?? t('Dashboard.no_plan')}
            icon={{
              type: IconType.Building,
              width: 56,
            }}
          />
        </div>
        <div className={dashboardStyles.purpleSvg}>
          <GeneralCustomersCard
            title={t('Dashboard.outlets')}
            comment={t('Dashboard.outlets_comment')}
            data={dashboardData?.numberOfOutlets ?? 0}
            icon={{
              type: IconType.Bag,
              width: 56,
            }}
          />
        </div>
        <div className={dashboardStyles.orangeSvg}>
          <GeneralCustomersCard
            title={t('Dashboard.total_transactions')}
            comment={t('Dashboard.total_transactions_comment')}
            data={formatNumber(dashboardData?.totalTransaction ?? 0, false, 0)}
            icon={{
              type: IconType.ExchangeDollar,
              width: 56,
            }}
          />
        </div>
        <div className={dashboardStyles.tealSvg}>
          <GeneralCustomersCard
            title={t('Dashboard.total_payment_volume')}
            comment={t('Dashboard.total_payment_volume_comment')}
            data={
              company?.settings?.currency
                ? formatCurrency(
                    dashboardData?.totalPaymentVolume ?? 0,
                    company.settings.currency as CurrencyCode,
                  )
                : String(dashboardData?.totalPaymentVolume ?? 0)
            }
            icon={{
              type: IconType.Coin,
              width: 56,
            }}
          />
        </div>
      </div>
      <div className={styles.profileOverviewContainer}>
        <div className={styles.profileOverview}>
          <Row justify='justify-between'>
            <Text size='l' weight='medium'>
              {t('Companies.profile_overview')}
            </Text>
            <div
              className={styles.editProfileButton}
              onClick={() => navigate(`/cdk/companies/${id}/${CompanyLinks.PROFILE}`)}
            >
              <div className={styles.blueSvg}>
                <Icon type={IconType.Pencil_Edit} width={16} height={16} fill='transparent' />
              </div>
              <Text size='s' weight='medium' color='blue'>
                {t('Companies.edit_profile')}
              </Text>
            </div>
          </Row>
          <div className={styles.profileOverviewContent}>
            <div>
              <Row justify='justify-between' align='start'>
                <Text size='s' weight='medium'>
                  {t('Companies.account_owner')}
                </Text>
                <Text size='s' weight='light'>
                  {companyOwner?.firstName + ' ' + companyOwner?.lastName}
                </Text>
              </Row>
              <Row justify='justify-between' align='start'>
                <Row noShrink fullWidth={false}>
                  <Text size='s' weight='medium'>
                    {t('Shared.email')}
                  </Text>
                </Row>
                <div className={styles.dynamicWidthContainer}>
                  <Text size='s' weight='light' className='truncate'>
                    {companyOwner?.email}
                  </Text>
                </div>
              </Row>
              <Row justify='justify-between' align='start'>
                <Row noShrink fullWidth={false}>
                  <Text size='s' weight='medium'>
                    {t('Shared.phone')}
                  </Text>
                </Row>
                <div className={styles.dynamicWidthContainer}>
                  <Text size='s' weight='light'>
                    {companyOwner?.phone}
                  </Text>
                </div>
              </Row>
            </div>
            <div>
              <Row justify='justify-between' align='start'>
                <Text size='s' weight='medium'>
                  {t('Shared.location')}:
                </Text>
                <Text size='s' weight='light'>
                  {company?.address?.country
                    ? Country.getCountryByCode(company?.address?.country)?.name ||
                      t('Errors.unknown_country')
                    : t('Errors.unknown_country')}
                </Text>
              </Row>
              {company?.createdAt && (
                <Row justify='justify-between' align='start'>
                  <Text size='s' weight='medium'>
                    {t('Accounts.account_created')}
                  </Text>
                  <Text size='s' weight='light'>
                    {convertDateToFormat(company?.createdAt)}
                  </Text>
                </Row>
              )}
              {dashboardData?.lastTransactionDate && (
                <Row justify='justify-between' align='start'>
                  <Text size='s' weight='medium'>
                    {t('Shared.last_transaction')}
                  </Text>
                  <Text size='s' weight='light'>
                    {formatDateToNow(dashboardData?.lastTransactionDate)}
                  </Text>
                </Row>
              )}
            </div>
          </div>
        </div>
        <Row fullWidth={false} gapSize='16' noShrink>
          <div className={styles.itemBox}>
            <Column align='center'>
              <Icon type={IconType.Box} width={48} height={48} fill='transparent' />
              <Column gapSize='none' align='center'>
                <Text size='m' weight='semi-bold'>
                  {formatNumber(dashboardData?.products ?? 0, false, 0)}
                </Text>
                <Text size='xs' weight='light-medium'>
                  {t('Dashboard.products')}
                </Text>
              </Column>
            </Column>
          </div>
          <div className={styles.itemBox}>
            <Column align='center'>
              <Icon type={IconType.Customers} width={48} height={48} fill='transparent' />
              <Column gapSize='none' align='center'>
                <Text size='m' weight='semi-bold'>
                  {formatNumber(dashboardData?.customers ?? 0, false, 0)}
                </Text>
                <Text size='xs' weight='light-medium'>
                  {t('Dashboard.customers')}
                </Text>
              </Column>
            </Column>
          </div>
          <div className={styles.itemBox}>
            <Column align='center'>
              <Icon type={IconType.BillPayment} width={48} height={48} fill='transparent' />
              <Column gapSize='none' align='center'>
                <Text size='m' weight='semi-bold'>
                  {formatNumber(dashboardData?.orders ?? 0, false, 0)}
                </Text>
                <Text size='xs' weight='light-medium'>
                  {t('Dashboard.orders')}
                </Text>
              </Column>
            </Column>
          </div>
        </Row>
      </div>
      {builds && builds.length > 0 && (
        <>
          <Divider />
          <Text size='l' weight='medium'>{`${t('Navigation.themes')}:`}</Text>
          <div className={styles.checkoutFlowGrid}>
            {builds.map((build, index) => (
              <CheckoutFlowCard key={index} build={build} organization={company} />
            ))}
          </div>
        </>
      )}
    </Column>
  );
}

export default CompanyDashboard;
